/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {motion} from 'framer-motion'
import {useOutsideClick} from '@chakra-ui/react'
import {useSearchSuggestions} from '@salesforce/commerce-sdk-react'
import {
    SearchIcon,
    CloseIcon
} from '@salesforce/retail-react-app/app/components/icons'

import {
    Box,
    Button,
    Flex,
    HStack,
    Input,
    InputGroup,
    InputLeftElement,
    Popover,
    PopoverContent,
    PopoverTrigger,
    useMultiStyleConfig,
    useMediaQuery
} from '@salesforce/retail-react-app/app/components/shared/ui'
import PropTypes from 'prop-types'
import React, {useEffect, useMemo, useRef, useState} from 'react'
import {searchAnimation} from '../../theme/animations/search-animation'
import SearchSuggestions from '../search/partials/search-suggestions'
import Overlay from '../overlay'
import {
    RECENT_SEARCH_KEY,
    RECENT_SEARCH_LIMIT,
    RECENT_SEARCH_MIN_LENGTH
} from '@salesforce/retail-react-app/app/constants'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {
    boldString,
    capitalize,
    getSessionJSONItem,
    setSessionJSONItem
} from '@salesforce/retail-react-app/app/utils/utils'
import debounce from 'lodash/debounce'
import {FormattedMessage, useIntl} from 'react-intl'
import {styleConfigChakraKeys} from '../../constants'
import {
    categoryUrlBuilder,
    contentUrlBuilder,
    productUrlBuilder,
    searchUrlBuilder
} from '../../utils/url'
import SearchProductTile from './partials/search-product-tile'
// import {
//     contentUrlBuilder
// } from '../../utils/url'
import Link from '../link'
import MobileSearch from './partials/mobile-search'

const formatSuggestions = (searchSuggestions, input = '') => {
    return {
        categorySuggestions:
            searchSuggestions?.categorySuggestions?.categories?.map(
                (suggestion) => {
                    return {
                        type: 'category',
                        id: suggestion.id,
                        link: categoryUrlBuilder({id: suggestion.id}),
                        name: boldString(suggestion.name, capitalize(input))
                    }
                }
            ),
        productSuggestions:
            searchSuggestions?.productSuggestions?.products?.map((product) => {
                return {
                    type: 'product',
                    ID: product.productId,
                    currency: product.currency,
                    price: product.price,
                    productId: product.productId,
                    name: boldString(product.productName, capitalize(input)),
                    link: productUrlBuilder({id: product.productId}),
                    image: product.c_image,
                    brand: product.c_brand,
                    regularPrice: product.c_originalPrice,
                    salePrice: product.price
                }
            }),
        phraseSuggestions:
            searchSuggestions?.categorySuggestions?.suggestedPhrases?.map(
                (phrase) => {
                    return {
                        type: 'phrase',
                        name: boldString(phrase.phrase, capitalize(input)),
                        link: searchUrlBuilder(phrase.phrase)
                    }
                }
            ),
        contentSuggestions: searchSuggestions?.contentSuggestions?.content?.map(
            (suggestion) => {
                return {
                    type: 'content',
                    id: suggestion.id,
                    link: contentUrlBuilder({id: suggestion.id}),
                    name: boldString(suggestion.name, capitalize(input))
                }
            }
        ),
        brandSuggestions:
            searchSuggestions?.brandSuggestions?.suggestedPhrases?.map(
                (suggestion) => {
                    return {
                        type: 'brand',
                        id: suggestion.phrase,
                        link: `/search?q=${suggestion.phrase}&refine=brand=${suggestion.phrase}`,
                        name: suggestion.phrase
                    }
                }
            )
    }
}

/**
 * The SearchInput component is a stylized
 * text input made specifically for use in
 * the application header.
 * @param  {object} props
 * @param  {object} ref reference to the input element
 * @return  {React.ReactElement} - SearchInput component
 */
const Search = ({searchOpen, searchShow, enableAnimation, ...props}) => {
    const [isOpen, setIsOpen] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')
    const navigate = useNavigation()
    const styles = useMultiStyleConfig(styleConfigChakraKeys.search)
    const [isDesktop] = useMediaQuery('(min-width: 1366px)')
    const searchSuggestion = useSearchSuggestions(
        {
            parameters: {
                q: searchQuery
            }
        },
        {
            enabled: searchQuery?.length >= RECENT_SEARCH_MIN_LENGTH
        }
    )
    const searchInputRef = useRef()
    const searchRef = useRef()
    const recentSearches = getSessionJSONItem(RECENT_SEARCH_KEY)
    const searchSuggestions = useMemo(() => {
        const formattedSuggestions = formatSuggestions(
            searchSuggestion.data,
            searchInputRef?.current?.value || ''
        )
        return formattedSuggestions
    }, [searchSuggestion])
    const intl = useIntl()

    // check if popover should open if we have suggestions
    useEffect(() => {
        shouldOpenPopover()
    }, [searchQuery, searchSuggestion.data])

    const searchSuggestionsAvailable =
        searchSuggestions &&
        (searchSuggestions?.categorySuggestions?.length ||
            searchSuggestions?.productSuggestions?.length)

    const saveRecentSearch = (searchText) => {
        // Get recent searches or an empty array if undefined.
        let searches = getSessionJSONItem(RECENT_SEARCH_KEY) || []

        // Check if term is already in the saved searches
        searches = searches?.filter((savedSearchTerm) => {
            return searchText?.toLowerCase() !== savedSearchTerm?.toLowerCase()
        })

        // Create a new array consisting of the search text and up to 4 other resent searches.
        // I'm assuming the order is newest to oldest.
        searches = [searchText, ...searches].slice(0, RECENT_SEARCH_LIMIT)

        // Replace the save resent search with the updated value.
        setSessionJSONItem(RECENT_SEARCH_KEY, searches)
    }

    const debouncedSearch = debounce((input) => {
        debouncedSearch.cancel()
        setSearchQuery(input)
    }, 300)

    const onSearchChange = async (e) => {
        const input = e.target.value
        if (input.length >= RECENT_SEARCH_MIN_LENGTH) {
            debouncedSearch(input)
        } else {
            setSearchQuery(e.target.value)
        }
    }

    const clearInput = () => {
        if (searchInputRef && searchInputRef.current) {
            searchInputRef.current.blur()
            setIsOpen(false)
            searchOpen(false)
        }
    }

    const onSubmitSearch = (e) => {
        e.preventDefault()
        // Avoid blank spaces to be searched
        let searchText = searchInputRef.current.value.trim()
        // Avoid empty string searches
        if (searchText.length < 1) {
            return
        }
        saveRecentSearch(searchText)
        clearInput()
        clearSearch()
        navigate(searchUrlBuilder(searchText))
    }

    const closeAndNavigate = (link) => {
        if (!link) {
            clearInput()
            setIsOpen(false)
            clearSearch()
        } else {
            clearInput()
            clearSearch()
            setIsOpen(false)
            navigate(link)
        }
    }

    const shouldOpenPopover = () => {
        // As per design we only want to show the popover if the input is focused and we have recent searches saved
        // or we have search suggestions available and have inputed some text (empty text in this scenario should show recent searches)
        if (
            searchSuggestionsAvailable &&
            searchInputRef?.current?.value.length > 0
        ) {
            setIsOpen(true)
        } else {
            setIsOpen(false)
        }
    }

    const onSearchInputChange = (e) => {
        onSearchChange(e)
        shouldOpenPopover()
    }

    const closeSearch = () => {
        setIsOpen(false)
        searchOpen(false)
    }

    const clearSearch = () => {
        setSearchQuery('')
        searchInputRef.current.value = ''
    }

    const onProductClick = () => {
        closeSearch()
        clearSearch()
    }

    useOutsideClick({
        ref: searchRef,
        handler: (e) => {
            const isMobileModal = e.target.closest(
                '#chakra-modal-search-mobile-modal'
            )
            if (isMobileModal) {
                return
            }

            if (
                !e.target.matches('#search-icon-btn') &&
                !e.target.matches('#search-icon-btn svg')
            ) {
                clearInput()
            } else {
                setIsOpen(false)
            }
        }
    })

    useEffect(() => {
        if (searchShow) {
            searchInputRef?.current?.focus()
        }
    }, [searchShow])

    return (
        <>
            {isDesktop && (
                <Box
                    {...styles.searchGroup}
                    ref={searchRef}
                    animation={
                        enableAnimation ? searchAnimation(searchShow) : 'none'
                    }
                    sx={{'.popup-open': {overflow: 'hidden'}}}
                >
                    {/* <HideOnMobile> */}
                    <Popover
                        isOpen={isOpen}
                        isLazy
                        initialFocusRef={searchInputRef}
                        variant="custom"
                    >
                        <PopoverTrigger>
                            <form onSubmit={onSubmitSearch}>
                                <HStack
                                    sx={{
                                        '#search-input::-webkit-search-cancel-button':
                                            {
                                                display: 'none'
                                            }
                                    }}
                                >
                                    <InputGroup {...styles.inputGroup}>
                                        <InputLeftElement
                                            pointerEvents="none"
                                            top="5px"
                                            left="15px"
                                            display={{
                                                base: 'none',
                                                xl: 'block'
                                            }}
                                        >
                                            <SearchIcon
                                                width="15px"
                                                _focus={{boxShadow: 'none'}}
                                            />
                                        </InputLeftElement>
                                        <Input
                                            autoComplete="off"
                                            id="search-input"
                                            onChange={(e) =>
                                                onSearchInputChange(e)
                                            }
                                            onFocus={() => shouldOpenPopover()}
                                            type="search"
                                            ref={searchInputRef}
                                            {...props}
                                            variant="filled"
                                            // value={searchQuery}
                                        />
                                        <Button
                                            {...styles.searchBtn}
                                            type="submit"
                                        >
                                            <FormattedMessage
                                                defaultMessage="Search"
                                                id="search.action.searchtxt"
                                            />
                                        </Button>
                                    </InputGroup>
                                </HStack>
                            </form>
                        </PopoverTrigger>

                        <PopoverContent
                            data-testid="sf-suggestion-popover"
                            {...styles.searchSuggestionDesk}
                            top="67px"
                            border="0"
                        >
                            <Overlay
                                zIndex="-3"
                                top="67px"
                                onClick={closeSearch}
                            />
                            <Flex
                                as={motion.div}
                                initial={{opacity: '0'}}
                                animate={{opacity: '1'}}
                                transition="opacity 0.2s linear"
                            >
                                <Box {...styles.desktopSearchContainer}>
                                    <SearchSuggestions
                                        closeAndNavigate={closeAndNavigate}
                                        recentSearches={recentSearches}
                                        searchSuggestions={searchSuggestions}
                                    />
                                </Box>
                                <Box {...styles.suggestionRight}>
                                    <Box {...styles.productWrapper}>
                                        {intl.formatMessage({
                                            defaultMessage: 'Products',
                                            id: 'use_product.message.products'
                                        })}
                                        <Box {...styles.closeIcon}>
                                            <CloseIcon onClick={closeSearch} />
                                        </Box>
                                    </Box>
                                    <Flex {...styles.productContainer}>
                                        {searchSuggestions?.productSuggestions
                                            ?.length
                                            ? searchSuggestions?.productSuggestions?.map(
                                                  (product, i) => {
                                                      if (i < 3) {
                                                          return (
                                                              <SearchProductTile
                                                                  hideCrossedPrice={
                                                                      true
                                                                  }
                                                                  width="33%"
                                                                  product={
                                                                      product
                                                                  }
                                                                  key={
                                                                      product.ID
                                                                  }
                                                                  onLinkClick={
                                                                      onProductClick
                                                                  }
                                                              />
                                                          )
                                                      }
                                                  }
                                              )
                                            : ''}
                                    </Flex>
                                    <Box>
                                        <Link
                                            type="tertiary"
                                            variant="unstyled"
                                            onLinkClick={onSubmitSearch}
                                            text={intl.formatMessage({
                                                defaultMessage:
                                                    'View all results',
                                                id: 'use_product.view.all.products'
                                            })}
                                            {...styles.viewAll}
                                        />
                                    </Box>
                                </Box>
                            </Flex>
                        </PopoverContent>
                    </Popover>
                    {/* </HideOnMobile> */}
                </Box>
            )}

            <MobileSearch
                searchShow={searchShow}
                onSubmitSearch={onSubmitSearch}
                onSearchInputChange={onSearchInputChange}
                shouldOpenPopover={shouldOpenPopover}
                searchInputRef={searchInputRef}
                searchQuery={searchQuery}
                isOpen={isOpen}
                searchSuggestions={searchSuggestions}
                closeAndNavigate={closeAndNavigate}
                onProductClick={onProductClick}
                recentSearches={recentSearches}
                {...props}
            />
        </>
    )
}

Search.displayName = 'SearchInput'

Search.propTypes = {
    searchOpen: PropTypes.func,
    searchShow: PropTypes.bool,
    enableAnimation: PropTypes.bool
}

export default Search
